import * as React from 'react';
import { CheckCircle, Info, Warning, Error } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core';
import { pallette, neutral } from '../theme/colors';
import { fontFamilies } from '../theme/text';

type AlertType = 'success' | 'info' | 'warning' | 'error';

export interface SecondaryAlertProps {
  /** The message in the body of the alert */
  message: string;
  /** The type of alert: success, info, warning, error */
  type: AlertType;
}

function bgColor(type: AlertType) {
  switch (type) {
    case 'success':
      return pallette.success.light;
    case 'info':
      return pallette.info.light;
    case 'warning':
      return pallette.warning.light;
    case 'error':
      return pallette.error.light;
  }
}
function borderColor(type: AlertType) {
  switch (type) {
    case 'success':
      return pallette.success.medium;
    case 'info':
      return pallette.info.medium;
    case 'warning':
      return pallette.warning.medium;
    case 'error':
      return pallette.error.medium;
  }
}
function iconColor(type: AlertType) {
  switch (type) {
    case 'success':
      return pallette.success.dark;
    case 'info':
      return pallette.info.dark;
    case 'warning':
      return pallette.warning.dark;
    case 'error':
      return pallette.error.dark;
  }
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '28px',
    borderRadius: '3px',
    padding: '0 8px',
    marginBottom: '20px',
    backgroundColor: (props: SecondaryAlertProps) => bgColor(props.type),
    border: (props: SecondaryAlertProps) => `1px solid ${borderColor(props.type)}`,
    '& svg': {
      marginRight: '8px',
      color: (props: SecondaryAlertProps) => iconColor(props.type),
      fontSize: '14px'
    }
  },
  message: {
    fontFamily: fontFamilies.heading,
    color: neutral[600],
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '-0.1px'
  }
});

/**
 * Secondary Alerts (a.k.a. Priority 2 Alerts) should be used for successful completion, completion error and other less urgent messages
 */
const SecondaryAlert = (props: SecondaryAlertProps) => {
  const classes = useStyles(props);

  const renderIcon = (type: AlertType) => {
    switch (type) {
      case 'success':
        return <CheckCircle />;
      case 'info':
        return <Info />;
      case 'warning':
        return <Warning />;
      case 'error':
        return <Error />;
    }
  };

  return (
    <div className={classes.root}>
      {renderIcon(props.type)}
      <span className={classes.message}>{props.message}</span>
    </div>
  );
};

export default SecondaryAlert;
